// EintragsListe.tsx — weitere Einträge einer Karte (Bank, Anschrift,
// Beziehungen …) im selben Aussehen wie der erste: Trennlinie statt rosa Karte.
//
// Seit 26.09.2026 wie die Vereine (VereinMitgliedschaften): „+ …" legt den
// Eintrag sofort an, jeder Eintrag hat unten rechts „Entfernen". Der frühere
// Entwurf mit „Fertig"/„Abbrechen" ist weg. Leere Einträge verwerfen die
// Payload-Builder beim Speichern; ein NEU angelegter Eintrag, dessen Felder
// man wieder komplett leert, verschwindet sofort.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { EintragDialog } from "./EintragDialog";
import {
  BeziehungsTabelle,
  HinzufuegenKnopf,
  type BeziehungsSpalten,
  type BeziehungsZeile,
  type TabellenAblage,
  type ZeilenAblage,
  type ZeilenKontakt,
} from "./BeziehungsTabelle";

/**
 * Steuerung von außen (Listen-Modus): `neu(vorlage)` öffnet das Popup mit
 * einem neuen Eintrag, vorbelegt mit `vorlage` — etwa „Ehepartner erfassen"
 * nach der Wahl „Verheiratet" (T3, 01.10.2026).
 */
export type EintragsListeSteuerung<T> = { neu: (vorlage?: Partial<T>) => void };

export type EintragsListeProps<T> = {
  items: T[];
  onChange: (next: T[]) => void;
  /** Leerer Eintrag für „+ …". */
  neu: () => T;
  /** Beschriftung ohne „+", z. B. „Weitere Bankverbindung". */
  hinzufuegenLabel: string;
  leerText?: string;
  /** Ohne Inhalt? Ein neuer Eintrag, der wieder leer wird, fällt weg. */
  istLeer?: (item: T) => boolean;
  /** Fehlertext oder null — angezeigt, sobald der Eintrag Inhalt hat. */
  pruefen?: (item: T) => string | null;
  renderFelder: (
    item: T,
    update: (patch: Partial<T>) => void,
    ctx: { index: number; entwurf: boolean },
  ) => React.ReactNode;
  /** Nur anzeigen: kein Hinzufügen, kein Entfernen. */
  nurLesen?: boolean;
  /**
   * Listen-Modus (Beziehungskarten, User 26.09.2026): Jeder Eintrag steht als
   * kompakte Zeile da; „+ …" und der Stift öffnen die Felder im Popup.
   */
  zeile?: (item: T) => {
    titel: React.ReactNode;
    zusatz?: React.ReactNode;
    /** Kleiner Text unter dem Namen. */
    hinweis?: React.ReactNode;
    /** Name und Rolle durchgestrichen (z. B. ausgeschieden). */
    durchgestrichen?: boolean;
    /** Spalten wie in der Akte — leer, wo die Anlage es nicht weiß. */
    anteile?: React.ReactNode;
    kontakt?: ZeilenKontakt;
    status?: string;
    /** Eigene Zieh-Daten/Kennzeichen statt der Eintrags-id (Paket A6). */
    ziehDaten?: string;
    ziehMarker?: string[];
    /** Ablage auf genau diese Zeile (Paket A7). */
    ablage?: ZeilenAblage;
  };
  /** Überschrift des Popups, z. B. „Person im Haushalt". */
  dialogTitel?: string;
  /**
   * Listen-Modus: Überschrift über der Tabelle. Mit ihr wandert „+ …" nach
   * rechts in die Kopfzeile — wie die Verknüpfungen der Kontaktakte.
   */
  listenTitel?: React.ReactNode;
  listenUntertitel?: React.ReactNode;
  /** Listen-Modus: Spaltenköpfe (Standard „Name" / „Rolle" + Kontaktmöglichkeit/Kontaktstatus). */
  spalten?: BeziehungsSpalten;
  /** Listen-Modus: weitere Aktion je Zeile (links neben Stift/Eimer). */
  zeilenExtra?: (item: T, index: number) => React.ReactNode;
  /** Listen-Modus: Nur-Lese-Zeilen am Ende (etwa aus einer anderen Karte gespiegelt). */
  fremdZeilen?: BeziehungsZeile[];
  /** Listen-Modus: ohne „+ …" (angelegt wird an anderer Stelle). */
  ohneHinzufuegen?: boolean;
  /**
   * Listen-Modus, Ziehen & Ablegen: Zeilen dieser Art sind ziehbar (Daten = die
   * Eintrags-id) bzw. werden mit `ablage` angenommen.
   */
  ziehArt?: string;
  ziehbar?: boolean;
  ablage?: TabellenAblage;
  /** Listen-Modus: öffnet das Popup von außen (siehe {@link EintragsListeSteuerung}). */
  steuerung?: React.MutableRefObject<EintragsListeSteuerung<T> | null>;
  /**
   * Listen-Modus: Klick auf „+ …" ruft dies statt das Popup direkt zu öffnen —
   * etwa um zuerst die Art abzufragen (Paket A5). Öffnen dann über `steuerung`.
   */
  onHinzufuegen?: () => void;
};

// Optik wie VereinMitgliedschaften (removeBtn, ghostBtn, divider).
const useStyles = makeStyles({
  liste: { display: "flex", flexDirection: "column", rowGap: "16px" },
  gruppe: { display: "flex", flexDirection: "column", rowGap: "16px" },
  trenner: {
    height: "1px",
    backgroundColor: REFINED.line,
    ...shorthands.margin("4px", "0"),
    ...shorthands.borderWidth("0"),
  },
  leer: { fontFamily: REFINED.fontUI, fontSize: "0.88rem", color: REFINED.ink3, fontStyle: "italic" },
  fuss: { display: "flex", justifyContent: "flex-end" },
  entfernen: {
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.82rem",
    ...shorthands.padding("6px", "12px"),
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.errBg },
  },
  ghost: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    ...shorthands.padding("8px", "16px"),
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  fehler: { fontFamily: REFINED.fontUI, fontSize: "0.82rem", color: REFINED.err },
});


// Einträge mit `id` (Beziehungen) behalten ihren Schlüssel beim Entfernen;
// Anschriften und Banken haben keine und laufen wie bisher über den Index.
const schluessel = (item: unknown, i: number): React.Key => {
  const id = (item as { id?: unknown } | null)?.id;
  return typeof id === "string" || typeof id === "number" ? id : i;
};

/**
 * Welche Einträge in dieser Sitzung per „+" entstanden sind und ob sie schon
 * Inhalt hatten — parallel zu `items`. Nur so lässt sich „neuer Eintrag wieder
 * geleert" von „bestehender Eintrag ohne Inhalt" unterscheiden.
 */
type Herkunft = { neu: boolean; befuellt: boolean };

export function EintragsListe<T extends object>(props: EintragsListeProps<T>) {
  return props.zeile ? <ListenModus {...props} zeile={props.zeile} /> : <InlineModus {...props} />;
}

/** Inline-Modus: alle Felder jedes Eintrags stehen direkt in der Karte. */
function InlineModus<T extends object>({
  items,
  onChange,
  neu,
  hinzufuegenLabel,
  leerText,
  istLeer,
  pruefen,
  renderFelder,
  nurLesen,
}: EintragsListeProps<T>) {
  const s = useStyles();

  // Asynchrone Rückrufe (IBAN erkannt, Adresse aufgelöst) brauchen den
  // AKTUELLEN Stand, nicht den vom Zeitpunkt, an dem sie entstanden sind.
  const aktuell = React.useRef({ items, onChange, istLeer });
  aktuell.current = { items, onChange, istLeer };

  const herkunft = React.useRef<Herkunft[]>([]);
  // Von außen geänderte Listen (Scan, Übernahme) gelten als bestehend.
  if (herkunft.current.length !== items.length) {
    herkunft.current = items.map((_, i) => herkunft.current[i] ?? { neu: false, befuellt: true });
  }

  const hinzufuegen = () => {
    herkunft.current = [...herkunft.current, { neu: true, befuellt: false }];
    onChange([...items, neu()]);
  };
  const entfernen = (i: number) => {
    herkunft.current = herkunft.current.filter((_, idx) => idx !== i);
    aktuell.current.onChange(aktuell.current.items.filter((_, idx) => idx !== i));
  };
  const aendern = (i: number) => (patch: Partial<T>) => {
    const { items: its, onChange: oc, istLeer: leer } = aktuell.current;
    const naechster = { ...its[i], ...patch };
    const h = herkunft.current[i];
    if (leer && h) {
      if (!leer(naechster)) {
        h.befuellt = true;
      } else if (h.neu && h.befuellt) {
        entfernen(i);
        return;
      }
    }
    oc(its.map((it, idx) => (idx === i ? naechster : it)));
  };

  return (
    <div className={s.liste}>
      {items.length === 0 && leerText && <div className={s.leer}>{leerText}</div>}
      {items.map((item, i) => {
        const fehler = pruefen && !(istLeer && istLeer(item)) ? pruefen(item) : null;
        return (
          <div key={schluessel(item, i)} className={s.gruppe} role="group" aria-label={`Eintrag ${i + 1}`}>
            <hr className={s.trenner} />
            {renderFelder(item, aendern(i), { index: i, entwurf: false })}
            {fehler && (
              <div className={s.fehler} role="alert">
                {fehler}
              </div>
            )}
            {!nurLesen && (
              <div className={s.fuss}>
                <button type="button" className={s.entfernen} onClick={() => entfernen(i)}>
                  Entfernen
                </button>
              </div>
            )}
          </div>
        );
      })}
      {!nurLesen && (
        <div className={s.fuss}>
          <button type="button" className={s.ghost} onClick={hinzufuegen}>
            + {hinzufuegenLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Listen-Modus: kompakte Zeilen, Bearbeiten und Anlegen im Popup. Der Entwurf
 * lebt nur im Dialog — „Abbrechen" verwirft ihn, ohne die Liste anzufassen.
 */
function ListenModus<T extends object>({
  items,
  onChange,
  neu,
  hinzufuegenLabel,
  leerText,
  istLeer,
  pruefen,
  renderFelder,
  nurLesen,
  zeile,
  dialogTitel,
  listenTitel,
  listenUntertitel,
  spalten,
  zeilenExtra,
  fremdZeilen,
  ohneHinzufuegen,
  ziehArt,
  ziehbar,
  ablage,
  steuerung,
  onHinzufuegen,
}: EintragsListeProps<T> & { zeile: NonNullable<EintragsListeProps<T>["zeile"]> }) {
  const s = useStyles();
  const [offen, setOffen] = React.useState<{ index: number | null; entwurf: T; nurAnsehen?: boolean } | null>(
    null,
  );

  const leer = offen ? (istLeer ? istLeer(offen.entwurf) : false) : true;
  const fehler = offen && !leer && pruefen ? pruefen(offen.entwurf) : null;

  const uebernehmen = () => {
    if (!offen || offen.nurAnsehen || leer || fehler) return;
    onChange(
      offen.index === null
        ? [...items, offen.entwurf]
        : items.map((it, i) => (i === offen.index ? offen.entwurf : it)),
    );
    setOffen(null);
  };
  const abbrechen = React.useCallback(() => setOffen(null), []);
  const oeffneNeu = () => (onHinzufuegen ? onHinzufuegen() : setOffen({ index: null, entwurf: neu() }));

  // Von außen öffnen: bei jedem Rendern frisch, damit `neu` aktuell ist.
  React.useEffect(() => {
    if (!steuerung) return;
    steuerung.current = {
      neu: (vorlage) => setOffen({ index: null, entwurf: { ...neu(), ...(vorlage ?? {}) } }),
    };
    return () => {
      steuerung.current = null;
    };
  });

  const zeilen: BeziehungsZeile[] = [
    ...items.map((item, i): BeziehungsZeile => {
      const z = zeile(item);
      const id = (item as { id?: unknown }).id;
      return {
        key: schluessel(item, i),
        name: z.titel,
        rolle: z.zusatz,
        hinweis: z.hinweis,
        durchgestrichen: z.durchgestrichen,
        anteile: z.anteile,
        kontakt: z.kontakt,
        status: z.status,
        extra: !nurLesen && zeilenExtra ? zeilenExtra(item, i) : undefined,
        // Auch in einer gesperrten Akte: dann nur zum Ansehen (User 28.09.2026).
        onBearbeiten: ({ nurAnsehen }) => setOffen({ index: i, entwurf: item, nurAnsehen: nurAnsehen || !!nurLesen }),
        onEntfernen: nurLesen ? undefined : () => onChange(items.filter((_, idx) => idx !== i)),
        ziehDaten: !nurLesen && ziehbar ? (z.ziehDaten ?? (typeof id === "string" ? id : undefined)) : undefined,
        ziehMarker: z.ziehMarker,
        ablage: nurLesen ? undefined : z.ablage,
      };
    }),
    ...(fremdZeilen ?? []),
  ];
  const mitKnopf = !nurLesen && !ohneHinzufuegen;
  const knopfImKopf = mitKnopf && !!listenTitel;

  return (
    <div className={s.liste}>
      {zeilen.length === 0 && !listenTitel && !ablage ? (
        leerText && <div className={s.leer}>{leerText}</div>
      ) : (
        <BeziehungsTabelle
          titel={listenTitel}
          untertitel={listenUntertitel}
          aktion={knopfImKopf ? <HinzufuegenKnopf label={hinzufuegenLabel} onClick={oeffneNeu} /> : undefined}
          spalten={spalten}
          zeilen={zeilen}
          leerText={leerText}
          ziehArt={ziehArt}
          ablage={nurLesen ? undefined : ablage}
        />
      )}
      {mitKnopf && !knopfImKopf && (
        <div className={s.fuss}>
          <HinzufuegenKnopf label={hinzufuegenLabel} onClick={oeffneNeu} />
        </div>
      )}
      {offen && (
        <EintragDialog
          titel={dialogTitel ?? hinzufuegenLabel}
          kicker={offen.index === null ? "Hinzufügen" : "Bearbeiten"}
          nurAnsehen={offen.nurAnsehen}
          fehler={fehler}
          uebernehmenGesperrt={leer || !!fehler}
          onUebernehmen={uebernehmen}
          onAbbrechen={abbrechen}
        >
          {renderFelder(
            offen.entwurf,
            (patch) => setOffen((o) => (o ? { ...o, entwurf: { ...o.entwurf, ...patch } } : o)),
            { index: offen.index ?? items.length, entwurf: offen.index === null },
          )}
        </EintragDialog>
      )}
    </div>
  );
}
